'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "className", "classes", "sx", "maxRows"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ComposerTextArea } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { mergeSlotProps } from "../internals/mergeSlotProps.mjs";
import { useChatComposerUtilityClasses } from "./chatComposerClasses.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatComposerTextArea');
const MAX_ROWS_STYLE = {
  minHeight: 'unset',
  margin: 'auto 0px',
  height: '28px'
};
const ChatComposerTextAreaStyled = styled('textarea', {
  name: 'MuiChatComposer',
  slot: 'TextArea',
  overridesResolver: (_, styles) => styles.textArea
})(({
  theme
}) => ({
  flex: 1,
  resize: 'none',
  border: 'none',
  borderRadius: 0,
  padding: theme.spacing(0.5),
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: theme.typography.body2.lineHeight,
  color: (theme.vars || theme).palette.text.primary,
  backgroundColor: 'transparent',
  outline: 'none',
  boxSizing: 'border-box',
  minHeight: 40,
  maxHeight: 200,
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  '&:disabled': {
    color: (theme.vars || theme).palette.text.disabled,
    cursor: 'not-allowed'
  },
  '&::placeholder': {
    color: (theme.vars || theme).palette.text.disabled
  }
}));
const ChatComposerTextArea = /*#__PURE__*/React.forwardRef(function ChatComposerTextArea(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatComposerTextArea'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx,
      maxRows
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatComposerUtilityClasses(classesProp);
  return /*#__PURE__*/_jsx(ComposerTextArea, _extends({
    ref: ref
  }, other, {
    slots: _extends({}, slots, {
      input: slots?.input ?? ChatComposerTextAreaStyled
    }),
    slotProps: _extends({}, slotProps, {
      input: mergeSlotProps(_extends({
        className: clsx(classes.textArea, className),
        sx
      }, maxRows != null ? {
        style: MAX_ROWS_STYLE,
        rows: 1
      } : {}), slotProps?.input)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatComposerTextArea.displayName = "ChatComposerTextArea";
process.env.NODE_ENV !== "production" ? ChatComposerTextArea.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  classes: PropTypes.object,
  className: PropTypes.string,
  /**
   * Maximum number of rows the textarea can expand to before it starts scrolling.
   * When set, the textarea starts at 1 row and auto-grows up to `maxRows`.
   */
  maxRows: PropTypes.number,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatComposerTextArea };